import React, { Suspense, lazy, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigationType } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { I18nextProvider } from 'react-i18next';
import i18n, { getStorageKeyForPath, getStoredLanguage } from './i18n';
import GlobalStyles from './styles/GlobalStyles';
import { CartProvider } from './context/CartContext';
import { AuthProvider } from './context/AuthContext';
import { AdminAuthProvider } from './context/AdminAuthContext';
import Header from './components/Layout/Header';
import Footer from './components/Layout/Footer';
import HomePage from './pages/Home';
import ProductsPage from './pages/Products';
import ProductDetailPage from './pages/ProductDetail';
import CategoriesPage from './pages/Categories';
import LoginPage from './pages/Login';
import RegisterPage from './pages/Register';
import AccountOrders from './pages/AccountOrders';
import AccountPage from './pages/Account';
import OrdersPage from './pages/Orders';
import OrderDetailPage from './pages/OrderDetail';
import OrderConfirmation from './pages/OrderConfirmation';
import QBOrderLookup from './pages/QBOrderLookup';
import QuickBitesMenu from './pages/QuickBitesMenu';
import CustomCakesMenu from './pages/CustomCakesMenu';
import BubbleTeaPickup from './pages/BubbleTeaPickup';
import BubbleTeaQRDisplay from './pages/BubbleTeaQRDisplay';
import PrivacyPolicy from './pages/PrivacyPolicy';
import CookiePolicy from './pages/CookiePolicy';
import TermsAndConditions from './pages/TermsAndConditions';
import RefundPolicy from './pages/RefundPolicy';
import QRRedirect from './pages/QRRedirect';
import CartSessionNotice from './components/Cart/CartSessionNotice';
import ProtectedRoute from './components/ProtectedRoute';

// Admin imports
import AdminLayout from './components/Admin/AdminLayout';
import AdminProtectedRoute from './components/Admin/AdminProtectedRoute';
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminProducts from './pages/admin/AdminProducts';
import AdminCategories from './pages/admin/AdminCategories';
import AdminOrders from './pages/admin/AdminOrders';
import AdminCustomers from './pages/admin/AdminCustomers';
import AdminCustomerDetail from './pages/admin/AdminCustomerDetail';
import AdminSettings from './pages/admin/AdminSettings';
import AdminLoyaltyPoints from './pages/admin/AdminLoyaltyPoints';
import AdminBubbleTeaOptions from './pages/admin/AdminBubbleTeaOptions';
import AdminInstantCakes from './pages/admin/AdminInstantCakes';
import AdminQBMenu from './pages/admin/AdminQBMenu';
import AdminQBOrders from './pages/admin/AdminQBOrders';
import AdminQBCategories from './pages/admin/AdminQBCategories';
import AdminBYOOptions from './pages/admin/AdminBYOOptions';
import AdminCakeOptions from './pages/admin/AdminCakeOptions';
import AdminBYOOrders from './pages/admin/AdminBYOOrders';

// Lazy-load checkout so Stripe.js is only fetched when the user visits /checkout
const CheckoutPage = lazy(() => import('./pages/CheckoutWithStripe'));
const QBCheckoutPage = lazy(() => import('./pages/QBCheckout'));
const BYOWizard = lazy(() => import('./pages/BYOWizard'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
    },
  },
});

// Sprint 7 fix-23: the customer storefront and admin console are mutually
// exclusive Route trees sharing one i18next instance. Header.tsx's and
// AdminLayout.tsx's own useLayoutEffect resync (fix-13) only forces a
// re-render of THEIR OWN subtree, so true siblings rendered separately in
// the Route element below (Footer, and anything reached through
// AdminLayout's <Outlet/>) never get a second render and can mount stuck in
// the previous surface's language. react-i18next's own `languageChanged`
// listener can't save them either -- it's bound inside a plain useEffect in
// the library, which hasn't run yet when Header's/AdminLayout's
// useLayoutEffect synchronously calls i18n.changeLanguage() in the same
// commit.
//
// Fix: resolve and correct the language in the render body, one level above
// <Routes>, so it happens before React evaluates ANY component in the
// newly-matched route tree -- every consumer's first-ever t() call already
// sees the corrected language. Must stay a render-body call (not an effect)
// or this reintroduces the identical timing race. Idempotent/no-op once the
// language already matches, so it's safe under StrictMode double-invocation
// and doesn't affect same-surface navigation or manual LanguageToggle
// clicks. Header.tsx/AdminLayout.tsx's own resync is left in place as a
// harmless no-op safety net for their isolated test coverage.
export function SurfaceLanguageSync({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const storageKey = getStorageKeyForPath(location.pathname);
  const storedLanguage = getStoredLanguage(storageKey);
  if (i18n.language !== storedLanguage) {
    i18n.changeLanguage(storedLanguage);
  }
  return <>{children}</>;
}

// task-029: scroll the window to the top whenever the route's pathname
// changes -- covers cross-page navigation (including checkout -> menu) and
// browser back/forward. Deliberately a useEffect (unlike
// SurfaceLanguageSync's render-body call above, which exists only to beat an
// i18next effect-timing race that doesn't apply here).
//
// Dependency array is [location.pathname] ONLY -- never the full `location`
// object or searchParams -- so this does not re-fire on/clobber
// QuickBitesMenu.tsx's own ?category=/?item=&type= deep-link scrollIntoView
// effects, which intentionally scroll mid-page on searchParams changes.
// navigationType is deliberately read via a ref (updated every render, not
// an effect dependency) rather than added to the dependency array: unlike
// location.pathname, navigationType DOES change independently of pathname --
// e.g. the very first in-app navigate() call on a page already showing
// ?searchParams flips navigationType from the initial 'POP' to 'PUSH' while
// the pathname itself stays put -- so including it in the deps array would
// make this effect re-fire on searchParams-only changes too, clobbering
// QuickBitesMenu's deep-link scrollIntoView effect. Reading the latest value
// via a ref keeps the effect's re-fire condition exactly as narrow as
// before (pathname-only) while still letting it branch on the up-to-date
// navigation type when it does fire.
//
// R12 fix-cycle-2 (this branch): in-app <Link>/navigate() transitions
// (PUSH/REPLACE) still force a genuinely instant jump -- see the inline
// override below. But browser back/forward (POP) is handled separately: an
// earlier version of this effect applied that same forced-instant override
// unconditionally, which raced the browser's own native
// `history.scrollRestoration` on POP navigations specifically (both write to
// scrollY around the same popstate/pageshow moment, with no guaranteed
// ordering), intermittently (~23%) leaving a small residual instead of
// landing exactly at 0. Per explicit product direction ("if this instance
// scroll to top is conflicting with browser native behaviour, correct it
// back to smooth scroll"), POP navigations no longer contest the browser's
// native restoration with a forced override -- they just issue one
// authoritative smooth scrollTo(0,0), whose target endpoint is 0 regardless
// of how it interleaves with native restoration.
export function ScrollToTop() {
  const location = useLocation();
  const navigationType = useNavigationType();
  const navigationTypeRef = useRef(navigationType);
  navigationTypeRef.current = navigationType;
  useEffect(() => {
    if (navigationTypeRef.current === 'POP') {
      // Browser back/forward: don't fight native scroll-restoration with a forced
      // override (see comment above) -- just scroll to top, letting it animate.
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      return;
    }
    // GlobalStyles.ts sets `html { scroll-behavior: smooth }` globally, and per the CSSOM
    // View spec, window.scrollTo's behavior:'auto' defers to that computed CSS value rather
    // than forcing an instant scroll. Temporarily force an inline override (which wins over
    // the stylesheet rule) so this jump is genuinely instant, then restore immediately --
    // synchronous save/set/scroll/restore in one tick, so nothing else (including other
    // components' own smooth scrollIntoView calls) can ever observe the override.
    const html = document.documentElement;
    const previousScrollBehavior = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';
    // Force a synchronous style flush before scrolling. Without this, Chromium doesn't
    // appear to recompute `scroll-behavior`'s used value between the two inline-style
    // mutations below (empirically verified via Playwright against the real built app --
    // omitting this line, the scroll still animated smoothly over ~300ms despite the
    // override, because the scroll dispatch read the *reverted* value once styles were
    // eventually recalculated rather than the temporary 'auto' value). Reading a layout
    // property forces the flush to happen right here, synchronously, in the same tick.
    void html.offsetHeight;
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    html.style.scrollBehavior = previousScrollBehavior;
  }, [location.pathname]);
  return null;
}

function App() {
  // task-029 fix-cycle-3: the browser's own native `history.scrollRestoration`
  // (default 'auto') is a second, uncontrolled writer to scrollY on
  // back/forward navigation, racing ScrollToTop's own POP-branch smooth
  // scrollTo(0,0) above -- this is what left the J97.6 e2e test with an
  // intermittent residual (~2px nonzero scrollY) after fix-cycle-2. Opting
  // into manual restoration removes that second writer entirely, so
  // ScrollToTop's own scrollTo is the sole authority on scroll position
  // across all navigation types. Runs once for the app's lifetime (empty dep
  // array, deliberately its own effect rather than folded into ScrollToTop's
  // pathname-keyed effect above) -- per user decision 2026-08-31 to use
  // manual restoration instead of continuing to time around the race.
  useEffect(() => {
    if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
  }, []);

  return (
    <I18nextProvider i18n={i18n}>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AdminAuthProvider>
          <CartProvider>
            <Router>
              <GlobalStyles />
              <ScrollToTop />
              <SurfaceLanguageSync>
              <Routes>
                {/* Customer routes */}
                <Route path="/" element={
                  <>
                    <Header />
                    <CartSessionNotice />
                    <HomePage />
                    <Footer />
                  </>
                } />
                <Route path="/products" element={
                  <>
                    <Header />
                    <CartSessionNotice />
                    <ProductsPage />
                    <Footer />
                  </>
                } />
                <Route path="/products/:id" element={
                  <>
                    <Header />
                    <CartSessionNotice />
                    <ProductDetailPage />
                    <Footer />
                  </>
                } />
                <Route path="/categories" element={
                  <>
                    <Header />
                    <CartSessionNotice />
                    <CategoriesPage />
                    <Footer />
                  </>
                } />
                <Route path="/checkout" element={
                  <>
                    <Header />
                    <CartSessionNotice />
                    <Suspense fallback={null}>
                      <CheckoutPage />
                    </Suspense>
                    <Footer />
                  </>
                } />
                <Route path="/order-confirmation/:orderId" element={
                  <>
                    <Header />
                    <OrderConfirmation />
                    <Footer />
                  </>
                } />
                <Route path="/login" element={
                  <>
                    <Header />
                    <CartSessionNotice />
                    <LoginPage />
                    <Footer />
                  </>
                } />
                <Route path="/register" element={
                  <>
                    <Header />
                    <CartSessionNotice />
                    <RegisterPage />
                    <Footer />
                  </>
                } />
                <Route path="/account" element={
                  <>
                    <Header />
                    <CartSessionNotice />
                    <ProtectedRoute>
                      <AccountPage />
                    </ProtectedRoute>
                    <Footer />
                  </>
                } />
                <Route path="/orders" element={
                  <>
                    <Header />
                    <CartSessionNotice />
                    <ProtectedRoute>
                      <OrdersPage />
                    </ProtectedRoute>
                    <Footer />
                  </>
                } />
                <Route path="/orders/:orderId" element={
                  <>
                    <Header />
                    <CartSessionNotice />
                    <ProtectedRoute>
                      <OrderDetailPage />
                    </ProtectedRoute>
                    <Footer />
                  </>
                } />
                <Route path="/account/orders" element={
                  <>
                    <Header />
                    <CartSessionNotice />
                    <ProtectedRoute>
                      <AccountOrders />
                    </ProtectedRoute>
                    <Footer />
                  </>
                } />

                {/* Quick Bites */}
                <Route path="/quick-bites" element={
                  <>
                    <Header />
                    <QuickBitesMenu />
                    <Footer />
                  </>
                } />
                <Route path="/quick-bites/checkout" element={
                  <>
                    <Header />
                    <Suspense fallback={null}>
                      <QBCheckoutPage />
                    </Suspense>
                    <Footer />
                  </>
                } />
                <Route path="/qb-orders/lookup" element={
                  <>
                    <Header />
                    <QBOrderLookup />
                    <Footer />
                  </>
                } />

                {/* Custom Cakes */}
                <Route path="/custom-cakes" element={
                  <>
                    <Header />
                    <CartSessionNotice />
                    <CustomCakesMenu />
                    <Footer />
                  </>
                } />
                <Route path="/custom-cakes/build-your-own" element={
                  <>
                    <Header />
                    <CartSessionNotice />
                    <Suspense fallback={null}>
                      <BYOWizard />
                    </Suspense>
                    <Footer />
                  </>
                } />
                <Route path="/custom-cakes/:id" element={
                  <>
                    <Header />
                    <CartSessionNotice />
                    <ProductDetailPage />
                    <Footer />
                  </>
                } />

                {/* Bubble Tea — keep existing route, now renders QuickBitesMenu */}
                <Route path="/bubble-tea" element={
                  <>
                    <Header />
                    <QuickBitesMenu />
                    <Footer />
                  </>
                } />
                <Route path="/bubble-tea/pickup/:token" element={
                  <>
                    <Header />
                    <BubbleTeaPickup />
                    <Footer />
                  </>
                } />
                <Route path="/bubble-tea/shop-display" element={<BubbleTeaQRDisplay />} />

                {/* QR code resolver — public, no auth guard */}
                <Route path="/qr/:token" element={<QRRedirect />} />

                {/* Legal pages */}
                <Route path="/privacy-policy" element={
                  <>
                    <Header />
                    <PrivacyPolicy />
                    <Footer />
                  </>
                } />
                <Route path="/cookie-policy" element={
                  <>
                    <Header />
                    <CookiePolicy />
                    <Footer />
                  </>
                } />
                <Route path="/terms" element={
                  <>
                    <Header />
                    <TermsAndConditions />
                    <Footer />
                  </>
                } />
                <Route path="/refund-policy" element={
                  <>
                    <Header />
                    <RefundPolicy />
                    <Footer />
                  </>
                } />

                {/* Admin routes */}
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin" element={
                  <AdminProtectedRoute>
                    <AdminLayout />
                  </AdminProtectedRoute>
                }>
                  <Route path="dashboard" element={<AdminDashboard />} />
                  <Route path="products" element={<AdminProducts />} />
                  <Route path="categories" element={<AdminCategories />} />
                  <Route path="orders" element={<AdminOrders />} />
                  <Route path="customers" element={<AdminCustomers />} />
                  <Route path="customers/:id" element={<AdminCustomerDetail />} />
                  <Route path="loyalty-points" element={<AdminLoyaltyPoints />} />
                  <Route path="settings" element={<AdminSettings />} />
                  <Route path="bubble-tea-options" element={<AdminBubbleTeaOptions />} />
                  <Route path="instant-cakes" element={<AdminInstantCakes />} />
                  <Route path="qb-menu" element={<AdminQBMenu />} />
                  <Route path="qb-orders" element={<AdminQBOrders />} />
                  <Route path="qb-categories" element={<AdminQBCategories />} />
                  <Route path="cake-options" element={<AdminCakeOptions />} />
                  <Route path="byo-options" element={<AdminBYOOptions />} />
                  <Route path="byo-orders" element={<AdminBYOOrders />} />
                  <Route index element={<AdminDashboard />} />
                </Route>
              </Routes>
              </SurfaceLanguageSync>
            </Router>
          </CartProvider>
        </AdminAuthProvider>
      </AuthProvider>
    </QueryClientProvider>
    </I18nextProvider>
  );
}

export default App;
